/**
 * Redux store configuration with persistence setup.
 *
 * This file configures the Redux store and sets up persistence for selected reducers using
 * `redux-persist`. The `auth` and `user` slices are persisted across sessions.
 *
 * @module store
 */

import { configureStore } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import authReducer from "./slices/authSlice";
import userReducer from "./slices/userSlice";
import { sessionMiddleware } from "./sessionMiddleware"; // Import the session middleware

/**
 * Redux Persist configuration for persisting selected slices of the state.
 * 
 * @type {Object}
 * @property {string} key - The key under which the state will be persisted.
 * @property {Object} storage - The storage medium (localStorage by default).
 * @property {Array} whitelist - Specifies which slices to persist (auth and user).
 */
const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth", "user"], // Persist 'auth' and 'user' slices
};

/**
 * Combines the reducers for auth and user slices.
 *
 * @type {Function}
 */
const rootReducer = combineReducers({
  auth: authReducer, // Auth slice reducer
  user: userReducer, // User slice reducer
});

/**
 * Creates a persisted reducer that wraps the rootReducer.
 *
 * @type {Function}
 */
const persistedReducer = persistReducer(persistConfig, rootReducer);

/**
 * Configures the Redux store with persisted reducer and middleware.
 *
 * @type {Object}
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disables serializable state checks for persisted state
    }).concat(sessionMiddleware), // Add the session middleware
});

/**
 * Set up the persistor object to manage persistence operations.
 *
 * @type {Object}
 */
export const persistor = persistStore(store);